import React, { useCallback, useContext, useMemo, useRef } from "react";
import { debounce, flatten } from "lodash";
import { z } from "zod";
import { formatCoordinates } from "common/utils/coordinates";
import { getInteractiveLayerIdsForSource } from "common-client/utils/customMapLayer";
import { SavedView } from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import useURLParsing from "../../../hooks/useURLParsing";
import { AuthContext } from "../../Authorization/AuthContext";
import { LayerContext } from "../layers";
import SavedViewsLayer, {
  viewsLayerId,
  viewsSourceId,
} from "../layers/savedViews";
import { CustomMapLayerValue, GeometryRef } from "./customMapLayer";
import {
  findRelevantFeature,
  LayerConfig,
  LayerHook,
  LayerHookBuilder,
  LayerId,
  Property,
} from "./types";
import {
  handleOnClickForCustomMap,
  handleOnHoverForCustomMap,
  makePropertyFromFeature,
  useFeatureClickState,
} from "./utils";

export type SavedViewsLayerConfig = LayerConfig<
  Array<Pick<SavedView, "id" | "query">>,
  Property | CustomMapLayerValue
> & { savedViews: Array<{ id: string }> };

export const getInteractiveLayerIds = (savedViewIds: Array<string>) => {
  return flatten(
    savedViewIds.map(id => {
      const base = `${viewsLayerId}-${id}` as LayerId;
      const interactive = getInteractiveLayerIdsForSource({ sourceId: base });
      return [base, ...interactive] as LayerId[];
    }),
  );
};

const useSavedViewsHook: LayerHookBuilder<SavedViewsLayerConfig> = ({
  config,
  helpers,
  mapLoaded,
}) => {
  const { addErrorToast } = useStatusToasts();
  const { visibleLayerIdsForGroup } = useContext(LayerContext);
  const { isPublic } = useContext(AuthContext);
  const addErrorToastCallback = useCallback(() => {
    addErrorToast(
      `Unable to load table view data. Please try again. If the issue continues, contact support@withforerunner.com`,
    );
  }, [addErrorToast]);

  const hoveredGeometry = useRef<Maybe<GeometryRef>>(null);
  const customObjParams = z.object({
    geometryId: z.string().optional(),
    customMapId: z.string().optional(),
  });
  const result = useURLParsing({
    routeName: "map",
    parser: customObjParams,
  });
  const geometryId = result?.geometryId;
  const customMapId = result?.customMapId;

  const interactiveLayerIds = getInteractiveLayerIds(
    visibleLayerIdsForGroup("savedViews"),
  );

  const clickedGeometry = useFeatureClickState({
    value: {
      geometryId,
      customMapId,
    },
    allInteractiveLayerIds: interactiveLayerIds,
    helpers,
    mapLoaded,
  });

  return useMemo<LayerHook>(() => {
    return {
      interactiveLayerIds,
      // Need to be lower than properties
      zIndex: -1,
      canHandleHover: () => !!config.interactive?.hover,
      canHandleClick: () => !!config.interactive?.click,
      onHover: ({ event }) => {
        if (hoveredGeometry.current) {
          helpers.emit({
            type: "removeFeatureState",
            data: { field: "hover", ...hoveredGeometry.current },
          });
          hoveredGeometry.current = null;
        }

        const allLayerIds = getInteractiveLayerIds(
          visibleLayerIdsForGroup("savedViews"),
        );
        const feature = findRelevantFeature({ event, layerId: allLayerIds });

        if (feature) {
          if (feature.properties.customMapId) {
            // guests can't yet interact with custom objects
            if (!isPublic) {
              helpers.setCursor("pointer");
              handleOnHoverForCustomMap({
                event,
                helpers,
                hoveredGeometry,
                feature,
              });
            }
          } else {
            helpers.setCursor("pointer");

            helpers.emit({
              type: "setTooltip",
              data: {
                x: event.point.x,
                y: event.point.y,
                text:
                  feature.properties.address ??
                  formatCoordinates({
                    latitude: feature.geometry.coordinates[1],
                    longitude: feature.geometry.coordinates[0],
                  }),
              },
            });
          }
        } else {
          const parcel = findRelevantFeature({ event, layerId: "parcels" });
          if (!parcel) {
            helpers.setCursor("grab");
          }
        }
      },
      onClick: ({ event }) => {
        const allLayerIds = getInteractiveLayerIds(
          visibleLayerIdsForGroup("savedViews"),
        );
        const feature = findRelevantFeature({ event, layerId: allLayerIds });

        if (feature) {
          if (feature.properties.customMapId) {
            // guests can't yet interact with custom objects
            if (!isPublic) {
              handleOnClickForCustomMap({
                helpers,
                clickedGeometry,
                feature,
                config,
                fromSavedView: "true",
              });
            } else {
              return false;
            }
          } else {
            config.onClick?.(makePropertyFromFeature(feature));
          }
          return true;
        } else {
          return false;
        }
      },
      render: () => {
        if (!config.savedViews.length) {
          return null;
        }

        return visibleLayerIdsForGroup("savedViews").map(savedViewId => (
          <SavedViewsLayer key={savedViewId} savedViewId={savedViewId} />
        ));
      },
      onFocusLost: () => {
        helpers.emit({ type: "removeTooltip", data: {} });
        if (hoveredGeometry.current) {
          helpers.emit({
            type: "removeFeatureState",
            data: { field: "hover", ...hoveredGeometry.current },
          });
          hoveredGeometry.current = null;
        }
      },

      shouldHandleLoadError: error =>
        (
          config.value?.map(({ id }) => `${viewsSourceId}-${id}`) ?? []
        ).includes(error.sourceId),
      onLoadError: () => {
        debounce(() => {
          addErrorToastCallback();
        }, 1000)();
      },
    };
  }, [
    config,
    addErrorToastCallback,
    visibleLayerIdsForGroup,
    helpers,
    interactiveLayerIds,
    clickedGeometry,
    isPublic,
  ]);
};

export default useSavedViewsHook;
